import '@openfonts/lato_latin'
import * as Sentry from '@sentry/react'
import { WebStorageStateStore } from 'oidc-client-ts'
import { lazy, Suspense, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router'

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-100015}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Bonds.com,_LLC.}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{BondsPro}_only_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{BondsPro}_need_to_be_licensed___{BondsPro}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{9_August_2027}____[v3]_[01]_MTgxNzc2NjAwMDAwMA==6a56da91d0d9d330b6dd29e964ebd1e0'
)

import config from './config'
import { isOpenFinRuntime } from './containers/OpenFin/helpers'
import { initHub } from './helpers/hub'
import { getInitialHydration } from './helpers/hydration'
import OidcWrapper from './routing/OidcWrapper'
import getStore from './store'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)
dayjs.extend(timezone)

// AG Grid css
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'

// Contexify css
import 'react-contexify/ReactContexify.css'

// Query Builder css
import 'react-querybuilder/dist/query-builder.scss'

// AG Grid modules
import { ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'

ModuleRegistry.registerModules([AllEnterpriseModule])

provideGlobalGridOptions({ theme: 'legacy' })

const store = getStore(getInitialHydration())
if (import.meta.env.MODE === 'development') {
  ;(window as any).__REDUX_STORE__ = store
}
initHub(store)

const replay =
  config.sentry.replay &&
  Sentry.replayIntegration({
    ...config.sentry.replay,
    workerUrl: '/worker.min.js'
  })

const integrations: any[] = [
  Sentry.reactRouterV7BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes
  }),
  Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] })
]

if (replay) {
  integrations.push(replay)
}

Sentry.init({
  dsn: 'https://5d88b9bcf3b767b9f0e88827c59e379d@o4506503129989120.ingest.sentry.io/4506507821580288',
  enableLogs: true,
  integrations,
  normalizeDepth: 5,
  ...config.sentry.baseProps
})

const oidcConfig = {
  authority: config.keycloak.domain,
  client_id: config.keycloak.client,
  domain: config.keycloak.domain,
  redirect_uri: config.keycloak.redirectUri,
  post_logout_redirect_uri: config.keycloak.redirectUri,
  silentRequestTimeoutInSeconds: config.api.refreshTokenTimeout,
  stopCheckSessionOnError: true,
  // OpenFin only: popouts share the token so they skip signinRedirect (whose
  // root-bounce drifted snapshot urls); browser keeps the default sessionStorage.
  ...(isOpenFinRuntime() && {
    userStore: new WebStorageStateStore({ store: window.localStorage })
  })
}

const params = new URLSearchParams(window.location.search)
if (!params.has('state')) {
  sessionStorage.setItem(
    'preAuthPath',
    window.location.pathname + window.location.search
  )
}

// Lazily loaded so the OpenFin chrome (+ its deps) is code-split out of the
// main bundle and only fetched on the /WindowFrame window.
const WindowFrame = lazy(
  () => import('./containers/OpenFin/Window/WindowFrame')
)

const errorFallback: Sentry.FallbackRender = ({ error, componentStack }) => {
  const err = error instanceof Error ? error.toString() : 'Unknown error'
  return (
    <>
      <h2>An error occurred:</h2>
      <pre className="error-boundary">
        {err}
        {componentStack}
      </pre>
    </>
  )
}

const isOpenFin = isOpenFinRuntime()
const container = document.getElementById('app')

if (container) {
  /*
     Fast-path the OpenFin window chrome (only needs the store + fin) ahead of
     the auth gate / WS / App shell. Guarded on isOpenFin so a non-OpenFin hit
     on /WindowFrame falls through to the SPA.
  */
  const isWindowFrame = isOpenFin && window.location.pathname === '/WindowFrame'

  createRoot(container).render(
    <Sentry.ErrorBoundary fallback={errorFallback}>
      <Provider store={store}>
        {isWindowFrame ? (
          <Suspense fallback={null}>
            <WindowFrame />
          </Suspense>
        ) : (
          <BrowserRouter>
            <OidcWrapper {...oidcConfig} />
          </BrowserRouter>
        )}
      </Provider>
    </Sentry.ErrorBoundary>
  )
}
